import { client } from "./amplifyClient";
import { getUserFromLocalStorage } from "./api/apiCore";

const CREATE_ERROR_LOG = /* GraphQL */ `
  mutation CreateErrorLog($input: CreateErrorLogInput!) {
    createErrorLog(input: $input) {
      id
    }
  }
`;

// Deduplication windows:
// - Non-critical: 10s — covers rapid re-fires from React re-renders / polling
// - Critical: 60s — covers ErrorBoundary retries while navigation is pending,
//   but still allows a new entry if the user navigates to another broken page
const DEDUP_WINDOW_NON_CRITICAL_MS = 10_000;
const DEDUP_WINDOW_CRITICAL_MS = 60_000;
const _recentErrors = new Map(); // signature -> timestamp

function _isDuplicate(message, options) {
  // Key on message + stack only — componentName intentionally excluded so that
  // the same error fired by both ErrorBoundary and window.onerror is deduplicated.
  const sig = `${options.critical ? "c" : "n"}|${message}|${String(options.stack ?? "").slice(0, 150)}`;
  const window = options.critical ? DEDUP_WINDOW_CRITICAL_MS : DEDUP_WINDOW_NON_CRITICAL_MS;
  const now = Date.now();
  const last = _recentErrors.get(sig);
  if (last && now - last < window) return true;
  _recentErrors.set(sig, now);
  // Prevent unbounded memory growth
  if (_recentErrors.size > 50) {
    _recentErrors.delete(_recentErrors.keys().next().value);
  }
  return false;
}

/**
 * Logs a frontend error to the DynamoDB ErrorLog table.
 * Fails silently — this function must never throw.
 *
 * @param {string|Error} error - Error message or Error object
 * @param {{ stack?: string, componentName?: string, critical?: boolean }} [options]
 */
export async function logError(error, options = {}) {
  try {
    // Comment out the next line to enable error logging on localhost for testing:
    if (window.location.hostname === "localhost") return;

    const user = getUserFromLocalStorage();
    if (!user) return; // Only log for authenticated users

    const errorMessage = error instanceof Error ? error.message : String(error);

    if (_isDuplicate(errorMessage, options)) return;

    await client.graphql({
      query: CREATE_ERROR_LOG,
      skipGlobalError: true,
      variables: {
        input: {
          userId: user.id ?? user.sub ?? "unknown",
          errorMessage: errorMessage.slice(0, 2000),
          errorStack: options.stack ? String(options.stack).slice(0, 5000) : null,
          url: window.location.href,
          componentName: options.componentName ?? null,
          critical: options.critical ? "true" : "false",
          baseType: "ErrorLog",
        },
      },
    });
  } catch (loggingError) {
    // Intentionally silent — error logging must never cause additional errors
    console.warn("[errorLogger] Failed to log error:", loggingError);
  }
}
